import {
  SUPPORT_AND_INSIGHT_SECTION,
  VINNY_CHAT_FEATURE,
  VYN_INSIGHTS_FEATURE,
  VINNY_CHAT_LICENCE,
  VYN_INSIGHTS_LICENCE,
} from '../permission/Components/UserManagement/SectionsData';

const normalize = (value) => String(value || '').toLowerCase().trim();

/**
 * Picks the role permission document that belongs to the logged-in user out of
 * the list returned by GET /rolepermission. A user is tied to a role document
 * through `role` (selectedRole) and `roleName.selectedRoleType`.
 */
export const findRolePermissions = (rolePermissionList, userConfig) => {
  if (!Array.isArray(rolePermissionList) || !userConfig) return null;

  // 1. Match by _id (if userConfig.roleName or userConfig.role or userConfig.roleId is an ObjectId)
  const possibleIds = [
    typeof userConfig?.roleName === 'string' ? userConfig.roleName : userConfig?.roleName?._id,
    userConfig?.roleId,
    userConfig?.roleNameId,
    userConfig?.role,
  ]
    .filter(Boolean)
    .map((id) => String(id).trim().toLowerCase());

  const matchedById = rolePermissionList.find((item) =>
    possibleIds.some((id) => String(item?._id).trim().toLowerCase() === id),
  );
  if (matchedById) return matchedById;

  // 2. Primary match by roleType (selectedRoleType / roleName)
  const roleType = normalize(
    typeof userConfig?.roleName === 'object' && userConfig?.roleName !== null
      ? userConfig?.roleName?.selectedRoleType ||
        userConfig?.roleName?.roleName ||
        userConfig?.roleName?.name ||
        userConfig?.roleName?.label
      : userConfig?.roleName || userConfig?.selectedRoleType,
  );
  if (roleType) {
    const matchedByRoleType = rolePermissionList.find(
      (item) =>
        normalize(item?.selectedRoleType) === roleType ||
        normalize(item?.roleName) === roleType,
    );
    if (matchedByRoleType) return matchedByRoleType;
  }

  // 3. Fallback match by role (selectedRole)
  const role = normalize(userConfig?.role);
  if (role) {
    const matchedByRole = rolePermissionList.find(
      (item) => normalize(item?.selectedRole) === role || normalize(item?.role) === role,
    );
    if (matchedByRole) return matchedByRole;
  }

  return null;
};

/**
 * Checks a single feature toggle under the "AI Assistants" section of a
 * role's capability object (permissions.cap). Access is denied unless the
 * Super Admin has explicitly switched the feature on for the user's role name,
 * so roles created before this section existed stay disabled.
 */
export const hasSupportAndInsightAccess = (modulePermissions, featureName) => {
  const section =
    modulePermissions?.[SUPPORT_AND_INSIGHT_SECTION] ||
    modulePermissions?.cap?.[SUPPORT_AND_INSIGHT_SECTION];
  //if no support and Insight then section is undefined
  if (!Array.isArray(section)) return false;
  // .some returns true if at least one element in the array satisfies the condition 
  return section.some(
    (item) => normalize(item?.feature) === normalize(featureName) && (item?.access === true || item?.access === 'true'),
  );
};

export const canUseVinnyChat = (modulePermissions, licensedFeatures) => {
  const hasLicence =
    licensedFeatures !== undefined
      ? hasLicenceFeature(licensedFeatures, VINNY_CHAT_LICENCE)
      : true;
  return hasLicence && hasSupportAndInsightAccess(modulePermissions, VINNY_CHAT_FEATURE);
};

export const canUseVynInsights = (modulePermissions, licensedFeatures) => {
  const hasLicence =
    licensedFeatures !== undefined
      ? hasLicenceFeature(licensedFeatures, VYN_INSIGHTS_LICENCE)
      : true;
  return hasLicence && hasSupportAndInsightAccess(modulePermissions, VYN_INSIGHTS_FEATURE);
};

// Licence (productLicenseKey) feature values for the "AI Assistants" bundle,
// re-exported so callers have a single import for every access check.
export { VINNY_CHAT_LICENCE, VYN_INSIGHTS_LICENCE };

export const hasLicenceFeature = (features, feature) => {
  if (!features || !Array.isArray(features)) return false;
  return features.some((item) => normalize(item) === normalize(feature));
};

